import React from 'react';
import { useDispatch } from 'react-redux';
import {
	Button,
	Card,
	CardActionArea,
	CardContent,
	CardMedia,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	Typography
} from '@mui/material';

import { closeDialog, openDialog } from 'app/store/fuse/dialogSlice';
import { Icon, useSettings, Utils } from 'app/main/components';
import { type MiddlewhereFile } from 'app/main/components/files-explorer/FilesUtils';
import FilesExplorerFileDialog from 'app/main/components/files-explorer/components/FilesExplorerFileDialog';

import TotemFilesViewer from '../files/TotemFilesViewer';

interface TotemDocumentsFoldersDialogProps {
	folderUid: string;
	folderTitle: string;
}

export function showTotemDocumentsFoldersDialog(props: TotemDocumentsFoldersDialogProps) {
	return openDialog({
		children: <TotemDocumentsFoldersDialog {...props} />,
		maxWidth: 'lg',
		fullWidth: true,
		scroll: 'body'
	});
}

export default function TotemDocumentsFoldersDialog(props: TotemDocumentsFoldersDialogProps) {
	const dispatch = useDispatch();
	const { t } = useSettings();

	const [currentDirectory, setCurrentDirectory] = React.useState<string | undefined>();
	const [filesList, setFilesList] = React.useState<MiddlewhereFile[]>([]);
	const [isLoading, setIsLoading] = React.useState(false);

	const [openSelectedFileDialog, setOpenSelectedFileDialog] = React.useState(false);
	const [selectedFile, setSelectedFile] = React.useState<MiddlewhereFile>();
	const [fileContent, setFileContent] = React.useState<string | undefined>();
	const [isLoadingFile, setIsLoadingFile] = React.useState(false);

	const goDirectoryUp = () => {
		if (currentDirectory?.includes('\\')) {
			const folders = currentDirectory?.split('\\');
			folders.pop();
			const path = folders.join('\\');
			setCurrentDirectory(path);
		} else {
			setCurrentDirectory(undefined);
		}
	};

	const handleOpen = file => {
		if (file.isDirectory) {
			if (currentDirectory) setCurrentDirectory(state => `${state}\\${file.name}`);
			else setCurrentDirectory(file.name);
		} else {
			setIsLoadingFile(true);
			setOpenSelectedFileDialog(true);
			setSelectedFile(file);
			Utils.executeApi.get({
				url: `/api/hrm/documents/${props.folderUid}/${file.name}${
					currentDirectory ? `?subfolderPath=${currentDirectory}` : ''
				}`,
				dispatch,
				successHandler: ({ data }) => {
					setFileContent(data.content);
					setIsLoadingFile(false);
				}
			});
		}
	};

	React.useEffect(() => {
		setIsLoading(true);
		Utils.executeApi.get({
			url: !currentDirectory
				? `/api/hrm/documents/${props.folderUid}`
				: `/api/hrm/documents/${props.folderUid}?subfolderPath=${currentDirectory}`,
			dispatch,
			successHandler: ({ data }) => {
				setFilesList(data.list);
				setIsLoading(false);
			}
		});
	}, [currentDirectory, dispatch, props.folderUid]);

	if (isLoading) return <></>;

	return (
		<>
			<DialogTitle>{props.folderTitle}</DialogTitle>
			<DialogContent>
				{filesList?.length > 0 ? (
					<Grid container spacing={2}>
						{currentDirectory && (
							<Grid size={{ md: 3, sm: 6, xs: 12 }}>
								<Card className="relative h-full rounded-lg" variant="outlined">
									<CardActionArea
										className="flex h-full flex-col justify-start"
										onClick={goDirectoryUp}
									>
										<CardMedia className="flex h-160 w-full items-center justify-center">
											<Icon icon="folder-arrow-up-outline" size={96} />
										</CardMedia>
										<CardContent>
											<Typography gutterBottom variant="subtitle2">
												{t('TOTEM_CARD_FOLDER_UP')}
											</Typography>
										</CardContent>
									</CardActionArea>
								</Card>
							</Grid>
						)}
						{filesList?.map((file, key) => (
							<Grid key={key} size={{ md: 3, sm: 6, xs: 12 }}>
								<TotemFilesViewer file={file} onOpen={() => handleOpen(file)} />
							</Grid>
						))}
					</Grid>
				) : (
					<Grid container justifyContent="center">
						<Grid>
							<Typography variant="h6">{t('VIEW_LIST_NO_ITEMS').toUpperCase()}</Typography>
						</Grid>
					</Grid>
				)}
			</DialogContent>
			<DialogActions>
				<Button size="large" onClick={() => dispatch(closeDialog())}>
					{t('DIALOG_ACTION_CLOSE')}
				</Button>
			</DialogActions>
			{selectedFile && fileContent ? (
				<FilesExplorerFileDialog
					open={openSelectedFileDialog}
					onClose={() => setOpenSelectedFileDialog(false)}
					// FIXME: basePath is wrong but required
					basePath={`${currentDirectory}\\${selectedFile.name}`}
					selectedFile={selectedFile}
					selectedFileContent={fileContent}
					isLoadingFile={isLoadingFile}
					disableDelete
					disableDownload
				/>
			) : (
				<></>
			)}
		</>
	);
}
